import type { EmbedSettingsResponseDto } from "@/lib/interfaces/embed";

/** Resolved embed visual tokens after merging saved settings, URL overrides, and org fallback. */
export type MergedEmbedVisualSettings = {
  color: string;
  customCss: string;
};

/**
 * Merge saved embed settings with optional URL query overrides (query parameters win over saved values).
 * `fallbackColor` is used when neither URL nor `base.color` is set (typically `organization.color` from the job payload).
 */
export function mergeEmbedVisualSettings(
  base: Partial<EmbedSettingsResponseDto>,
  options?: {
    urlParams?: URLSearchParams | null;
    fallbackColor?: string;
  },
): MergedEmbedVisualSettings {
  const url = options?.urlParams ?? null;
  const fallback = (options?.fallbackColor ?? "").trim() || "#3182ce";

  const color = (url?.get("color")?.trim() || base.color?.trim() || fallback).trim() || "#3182ce";

  const customCss = url?.get("customCss") ?? base.customCss ?? "";

  return {
    color,
    customCss: typeof customCss === "string" ? customCss : "",
  };
}

export function getEmbedJobCombinedCss(dynamicCss: string, customCss: string): string {
  return dynamicCss + (customCss || "");
}

/**
 * Injected rules for accent color and hook classes (custom CSS can override with higher specificity or !important).
 */
export function buildEmbedJobDynamicCss(settings: { color: string }): string {
  return `
    .embed-job-root {
      --embed-job-accent: ${settings.color};
    }

    .embed-job-header-cta,
    .embed-job-hero-cta {
      border-radius: 8px;
    }

    .embed-job-hero-media {
      border-radius: 8px;
      overflow: hidden;
    }

    .embed-job-form {
      border-radius: 8px;
    }

    .embed-job-submit {
      border-radius: 8px;
    }

    .embed-job-similar-card {
      border-radius: 8px;
    }
  `;
}

/** Preview panel root class — must match the scroll container in `IFrameSettingsJobPreview`. */
export const EMBED_JOB_PREVIEW_SCOPE_CLASS = "iframe-settings-job-preview-root";

/** Wrap combined embed CSS so it only applies under the settings preview scroll root. */
export function wrapEmbedJobPreviewScopedCss(innerCss: string): string {
  return `@scope (.${EMBED_JOB_PREVIEW_SCOPE_CLASS}) {\n${innerCss}\n}`;
}

export function buildEmbedJobInjectedStyleContent(
  dynamicCss: string,
  customCss: string,
  options: { scopeToPreviewRoot: boolean },
): string {
  const combined = getEmbedJobCombinedCss(dynamicCss, customCss);
  if (options.scopeToPreviewRoot) {
    return wrapEmbedJobPreviewScopedCss(combined);
  }
  return combined;
}
